import isEqual from 'lodash/isEqual'

/**
 * Creates a props comparison function for React.memo with selective deep comparison
 *
 * Two-pass approach:
 * 1. Shallow compare all props (cheap) - early exit if non-deep-compare prop changes
 * 2. Deep compare only specified props that failed shallow comparison (expensive)
 *
 * This avoids expensive deep comparisons when unnecessary while still handling
 * props passed as inline objects/arrays with the same values.
 *
 * @param {Object} options - Configuration options
 * @param {string[]} options.deepCompareProps - Array of prop names that require deep comparison (required, non-empty)
 * @returns {Function} Comparison function for React.memo: (prevProps, nextProps) => boolean
 * @throws {Error} If deepCompareProps is not provided, not an array, or is empty
 *
 * @example
 * const MyComponent = React.memo(
 *   ({ widths, sources, src, alt }) => { ... },
 *   createPropsComparator({ deepCompareProps: ['widths', 'sources'] })
 * )
 */
export const createPropsComparator = (options) => {
    // Validate options
    if (
        !options ||
        !options.deepCompareProps ||
        !Array.isArray(options.deepCompareProps) ||
        options.deepCompareProps.length === 0
    ) {
        throw new Error('createPropsComparator requires options.deepCompareProps array')
    }

    const deepCompareProps = options.deepCompareProps

    return (prevProps, nextProps) => {
        const prevKeys = Object.keys(prevProps)
        const nextKeys = Object.keys(nextProps)

        // Different number of props means props changed
        if (prevKeys.length !== nextKeys.length) {
            return false
        }

        const propsNeedingDeepCompare = []

        // First pass: shallow compare all props (cheap)
        for (const key of prevKeys) {
            // Prop was removed
            if (!(key in nextProps)) {
                return false
            }

            const prevValue = prevProps[key]
            const nextValue = nextProps[key]

            // Shallow comparison (reference equality)
            if (prevValue === nextValue) {
                continue
            }

            // Shallow comparison failed
            if (deepCompareProps.includes(key)) {
                // Queue for deep comparison
                propsNeedingDeepCompare.push(key)
            } else {
                // Non-deep-compare prop changed - early exit without expensive deep comparison
                return false
            }
        }

        // Second pass: deep compare only props that failed shallow comparison (expensive)
        for (const key of propsNeedingDeepCompare) {
            if (!isEqual(prevProps[key], nextProps[key])) {
                return false
            }
        }

        return true
    }
}
